import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  InputAdornment,
  Pagination,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar
} from '@mui/material';
import {
  People as PeopleIcon,
  Search as SearchIcon,
  School as SchoolIcon,
  LocationOn as LocationOnIcon,
  Business as BusinessIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface Employee {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  department: string;
  office?: string;
  isActive: boolean;
  isLocked: boolean;
  company: {
    name: string;
  };
}

interface Course {
  _id: string;
  title: string;
  unenrolledCount: number;
}

interface Office {
  name: string;
  count: number;
}

interface Department {
  name: string;
  count: number;
}

interface EmployeeCourseRow {
  employee: Employee;
  course: {
    _id: string;
    title: string;
  };
  lastReminderSent?: string | null;
}

const CompanyEmployeesPendingCourses: React.FC = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState<EmployeeCourseRow[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [officeFilter, setOfficeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  
  // Estados para el diálogo de confirmación y envío de correos
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchData();
  }, [page, searchTerm, departmentFilter, officeFilter, statusFilter, courseFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.getEmployeesPendingCourses({
        page,
        limit: 10,
        search: searchTerm,
        department: departmentFilter,
        office: officeFilter,
        status: statusFilter,
        course: courseFilter
      });
      
      setRows(response.rows);
      setCourses(response.courses);
      setOffices(response.offices || []);
      setDepartments(response.departments || []);
      setTotalPages(response.pagination.pages);
      setTotalRows(response.pagination.total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  };


  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'error';
  };

  const getStatusLabel = (isActive: boolean) => {
    return isActive ? 'Activo' : 'Inactivo';
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSendReminders = async () => {
    try {
      setSendingReminders(true);
      const result = await api.sendCourseReminders({
        search: searchTerm,
        department: departmentFilter,
        office: officeFilter,
        status: statusFilter,
        course: courseFilter
      });
      
      setSnackbar({
        open: true,
        message: `Se enviaron ${result.sent} correos exitosamente${result.failed > 0 ? `. ${result.failed} correos fallaron` : ''}`,
        severity: result.failed > 0 ? 'error' : 'success'
      });
      
      setReminderDialogOpen(false);
    } catch (err: any) {
      const isTimeout =
        err.code === 'ECONNABORTED' ||
        String(err.message || '').toLowerCase().includes('timeout');
      const msg =
        err.response?.data?.message ||
        (isTimeout
          ? 'Tiempo de espera agotado: hay muchos correos en cola. Reintenta; si persiste, envía por grupos usando filtros o contacta soporte.'
          : err.message || 'Error enviando recordatorios');
      setSnackbar({
        open: true,
        message: msg,
        severity: 'error'
      });
    } finally {
      setSendingReminders(false);
    }
  };

  if (loading && rows.length === 0) {
    return (
      <Container maxWidth="xl">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography>Cargando datos...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <SchoolIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Empleados con Cursos Pendientes
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EmailIcon />}
            onClick={() => setReminderDialogOpen(true)}
            disabled={totalRows === 0}
            sx={{ textTransform: 'none' }}
          >
            Enviar Recordatorios
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Filtros principales en la zona superior */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{
              display: 'flex',
              gap: 3,
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <TextField
                sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' } }}
                label="Buscar empleados"
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl sx={{ flex: { xs: '1 1 100%', md: '1 1 200px' } }}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  label="Estado"
                >
                  <MenuItem value="all">Todos</MenuItem>
                  <MenuItem value="active">Activos</MenuItem>
                  <MenuItem value="inactive">Inactivos</MenuItem>
                  <MenuItem value="locked">Bloqueados</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">
                Total: {totalRows} registros
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Layout principal: Panel de refinamiento a la izquierda, Grilla a la derecha */}
        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Paneles de refinamiento - IZQUIERDA */}
          <Box sx={{ flex: { xs: '1', md: '0 0 300px' }, minWidth: { xs: '100%', md: '300px' }, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Panel de refinamiento de cursos */}
            <Card 
              sx={{ 
                boxShadow: 3
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <SchoolIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Cursos Pendientes
                    </Typography>
                  </Box>
                  {courseFilter && (
                    <Button
                      size="small"
                      onClick={() => {
                        setCourseFilter('');
                        setPage(1);
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Limpiar
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ maxHeight: '600px', overflowY: 'auto', pr: 1 }}>
                  {courses.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No hay cursos disponibles
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {/* Opción "Todos" */}
                      <Button
                        fullWidth
                        variant={courseFilter === '' ? 'contained' : 'outlined'}
                        onClick={() => {
                          setCourseFilter('');
                          setPage(1);
                        }}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          py: 1.5,
                          px: 2,
                          borderRadius: 2,
                          textAlign: 'left',
                          borderWidth: courseFilter === '' ? 2 : 1,
                          '&:hover': {
                            borderWidth: 2,
                            transform: 'translateY(-1px)',
                            boxShadow: 2
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="body1" fontWeight="bold">
                            Todos los cursos
                          </Typography>
                          <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                            <PeopleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {totalRows} empleados pendientes
                            </Typography>
                          </Box>
                        </Box>
                      </Button>

                      {/* Lista de cursos */}
                      {courses
                        .filter(course => course.unenrolledCount > 0)
                        .sort((a, b) => b.unenrolledCount - a.unenrolledCount)
                        .map((course) => (
                          <Button
                            key={course._id}
                            fullWidth
                            variant={courseFilter === course._id ? 'contained' : 'outlined'}
                            color={courseFilter === course._id ? 'primary' : 'inherit'}
                            onClick={() => {
                              setCourseFilter(courseFilter === course._id ? '' : course._id);
                              setPage(1);
                            }}
                            sx={{
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              py: 1.5,
                              px: 2,
                              borderRadius: 2,
                              textAlign: 'left',
                              borderWidth: courseFilter === course._id ? 2 : 1,
                              '&:hover': {
                                borderWidth: 2,
                                transform: 'translateY(-1px)',
                                boxShadow: 2,
                                backgroundColor: courseFilter === course._id 
                                  ? 'primary.dark' 
                                  : 'action.hover'
                              },
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            <Box sx={{ width: '100%' }}>
                              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5} gap={1}>
                                <Typography 
                                  variant="body2" 
                                  fontWeight={courseFilter === course._id ? 'bold' : 'medium'}
                                  sx={{
                                    textAlign: 'left',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    flex: 1
                                  }}
                                >
                                  {course.title}
                                </Typography>
                                <Chip
                                  label={course.unenrolledCount}
                                  size="small"
                                  color={courseFilter === course._id ? 'primary' : course.unenrolledCount > 10 ? 'error' : course.unenrolledCount > 5 ? 'warning' : 'default'}
                                  sx={{
                                    fontWeight: 'bold',
                                    minWidth: '45px',
                                    height: '24px',
                                    flexShrink: 0
                                  }}
                                />
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                empleados pendientes
                              </Typography>
                            </Box>
                          </Button>
                        ))}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Panel de refinamiento de Sede */}
            <Card 
              sx={{ 
                boxShadow: 3
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationOnIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Sede
                    </Typography>
                  </Box>
                  {officeFilter && (
                    <Button
                      size="small"
                      onClick={() => {
                        setOfficeFilter('');
                        setPage(1);
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Limpiar
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ maxHeight: '300px', overflowY: 'auto', pr: 1 }}>
                  {offices.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No hay sedes disponibles
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Button
                        fullWidth
                        variant={officeFilter === '' ? 'contained' : 'outlined'}
                        onClick={() => {
                          setOfficeFilter('');
                          setPage(1);
                        }}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          py: 1,
                          px: 2,
                          borderRadius: 2,
                          borderWidth: officeFilter === '' ? 2 : 1,
                          '&:hover': {
                            borderWidth: 2,
                            transform: 'translateY(-1px)',
                            boxShadow: 2
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold">
                          Todas las sedes
                        </Typography>
                      </Button>
                      {(() => {
                        // Separar "Sin Sede" del resto
                        const sinSede = offices.find(office => office.name === 'Sin Sede');
                        const otrasSedes = offices.filter(office => office.name !== 'Sin Sede');
                        
                        // Ordenar otras sedes por count
                        const sedesOrdenadas = otrasSedes.sort((a, b) => b.count - a.count);
                        
                        // Agregar "Sin Sede" al final si existe
                        const todasLasSedes = [...sedesOrdenadas];
                        if (sinSede) {
                          todasLasSedes.push(sinSede);
                        }
                        
                        return todasLasSedes;
                      })().map((office) => (
                          <Button
                            key={office.name}
                            fullWidth
                            variant={officeFilter === office.name ? 'contained' : 'outlined'}
                            color={officeFilter === office.name ? 'primary' : 'inherit'}
                            onClick={() => {
                              setOfficeFilter(officeFilter === office.name ? '' : office.name);
                              setPage(1);
                            }}
                            sx={{
                              justifyContent: 'space-between',
                              textTransform: 'none',
                              py: 1,
                              px: 2,
                              borderRadius: 2,
                              borderWidth: officeFilter === office.name ? 2 : 1,
                              '&:hover': {
                                borderWidth: 2,
                                transform: 'translateY(-1px)',
                                boxShadow: 2
                              },
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            <Typography 
                              variant="body2" 
                              fontWeight={officeFilter === office.name ? 'bold' : 'medium'}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                                textAlign: 'left'
                              }}
                            >
                              {office.name}
                            </Typography>
                            <Chip
                              label={office.count}
                              size="small"
                              color={officeFilter === office.name ? 'primary' : 'default'}
                              sx={{
                                ml: 1,
                                fontWeight: 'bold',
                                minWidth: '40px',
                                flexShrink: 0
                              }}
                            />
                          </Button>
                        ))}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Panel de refinamiento de Departamento */}
            <Card 
              sx={{ 
                boxShadow: 3,
                position: 'sticky',
                top: 20
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BusinessIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Departamento
                    </Typography>
                  </Box>
                  {departmentFilter && (
                    <Button
                      size="small"
                      onClick={() => {
                        setDepartmentFilter('');
                        setPage(1);
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Limpiar
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ maxHeight: '300px', overflowY: 'auto', pr: 1 }}>
                  {departments.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No hay departamentos disponibles
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Button
                        fullWidth
                        variant={departmentFilter === '' ? 'contained' : 'outlined'}
                        onClick={() => {
                          setDepartmentFilter('');
                          setPage(1);
                        }}
                        sx={{
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          py: 1,
                          px: 2,
                          borderRadius: 2,
                          borderWidth: departmentFilter === '' ? 2 : 1,
                          '&:hover': {
                            borderWidth: 2,
                            transform: 'translateY(-1px)',
                            boxShadow: 2
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold">
                          Todos los departamentos
                        </Typography>
                      </Button>
                      {departments
                        .sort((a, b) => b.count - a.count)
                        .map((dept) => (
                          <Button
                            key={dept.name}
                            fullWidth
                            variant={departmentFilter === dept.name ? 'contained' : 'outlined'}
                            color={departmentFilter === dept.name ? 'primary' : 'inherit'}
                            onClick={() => {
                              setDepartmentFilter(departmentFilter === dept.name ? '' : dept.name);
                              setPage(1);
                            }}
                            sx={{
                              justifyContent: 'space-between',
                              textTransform: 'none',
                              py: 1,
                              px: 2,
                              borderRadius: 2,
                              borderWidth: departmentFilter === dept.name ? 2 : 1,
                              '&:hover': {
                                borderWidth: 2,
                                transform: 'translateY(-1px)',
                                boxShadow: 2
                              },
                              transition: 'all 0.2s ease-in-out'
                            }}
                          >
                            <Typography 
                              variant="body2" 
                              fontWeight={departmentFilter === dept.name ? 'bold' : 'medium'}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1,
                                textAlign: 'left'
                              }}
                            >
                              {dept.name}
                            </Typography>
                            <Chip
                              label={dept.count}
                              size="small"
                              color={departmentFilter === dept.name ? 'primary' : 'default'}
                              sx={{
                                ml: 1,
                                fontWeight: 'bold',
                                minWidth: '40px',
                                flexShrink: 0
                              }}
                            />
                          </Button>
                        ))}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Grilla de empleados - DERECHA */}
          <Box sx={{ flex: { xs: '1', md: '1' }, minWidth: 0 }}>
            <Card>
              <CardContent>
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Empleado</TableCell>
                        <TableCell>Sede</TableCell>
                        <TableCell>Departamento</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Curso</TableCell>
                        <TableCell>Último Recordatorio</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                              No se encontraron empleados con cursos pendientes
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        rows.map((row, index) => {
                          const formatDate = (dateString: string | null | undefined) => {
                            if (!dateString) return 'Nunca';
                            const date = new Date(dateString);
                            return date.toLocaleString('es-ES', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                          };

                          return (
                            <TableRow key={`${row.employee._id}-${row.course._id}-${index}`} hover>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={2}>
                                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                                    {row.employee.firstName.charAt(0)}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                      {row.employee.firstName} {row.employee.lastName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {row.employee.email}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {row.employee.office || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {row.employee.department || 'N/A'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getStatusLabel(row.employee.isActive)}
                                  color={getStatusColor(row.employee.isActive)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {row.course.title}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color={row.lastReminderSent ? 'text.primary' : 'text.secondary'}>
                                  {formatDate(row.lastReminderSent)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Paginación */}
                {totalPages > 1 && (
                  <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>

      {/* Diálogo de confirmación para enviar recordatorios */}
      <Dialog
        open={reminderDialogOpen}
        onClose={() => !sendingReminders && setReminderDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon color="primary" />
            <Typography variant="h6">
              Enviar Recordatorios de Cursos
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se enviará un correo electrónico a <strong>{totalRows} empleado{totalRows !== 1 ? 's' : ''}</strong> de la lista filtrada actual, 
            invitándolos a inscribirse en {courseFilter ? 'el curso seleccionado' : 'los cursos disponibles'}.
          </DialogContentText>
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              El correo incluirá:
            </Typography>
            <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
              <li>Invitación a inscribirse en el curso pendiente</li>
              <li>Enlace directo a la plataforma de aprendizaje</li>
              <li>Información sobre el curso disponible</li>
            </ul>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Los correos se enviarán a los empleados que cumplan con los filtros actuales aplicados.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setReminderDialogOpen(false)} 
            disabled={sendingReminders}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleSendReminders} 
            variant="contained" 
            color="primary"
            disabled={sendingReminders}
            startIcon={<EmailIcon />}
          >
            {sendingReminders ? 'Enviando...' : 'Enviar Recordatorios'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CompanyEmployeesPendingCourses;
